// Configuration
import config from '../../../config';

// Models
import {  defaultStandardHeaders } from '../models/StandardHeaders';
import { StandardResponse, defaultStandardResponse } from '../models/StandardResponse';
import { StatusCodesConstants } from '../utilities/appConstants'
// Utilities
import { getStorageItem } from './applicationStorage';

// Constants
import { OrganizationConstants, AuthenticationConstants } from './appConstants';

interface FetchWrapOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  credentials?: RequestCredentials
}
  
interface FetchWrapParams {
  url: string;
  options: FetchWrapOptions;
  token: string;
  auth?: boolean;
  cookies?: boolean;
  headerOptions?: Record<string, string>;
  noContentType?: boolean;
  contentType?: string;
  noResponseData?: boolean;
  retries?: number;
  delay?: number;
  conversationId?: string | undefined; 
}

// This file supports basic fetch wrapping utilities, additionally with functions that wrap fetch calls for retry functionality.

/**
 * @description Allows the ability to pull a baseUri from configuration. Allows for the ability to support multi-env application base uris.
 * @param {string} type either message|settings
 * @return {string} string uri 
 */
export const baseUri = (type = '') => {
  let result = '';
  if (type && type.length > 0) {
    result = config.someRandomAPIEntry; // Configure this entry for api usage
  }    
  return result;
};


/**
 * @description Grabs the current API reference being accessed based on environment.
 * @return {string} 
 */
export const gcBaseUri = () => {
  const api = getStorageItem(OrganizationConstants.UrlApi, undefined, undefined, sessionStorage);
  return api;
};

/**
 * @description Grabs the current access token available.
 * @return {string} 
 */
export const gcAccessToken = () => {
  const token = getStorageItem(AuthenticationConstants.CloudCSPToken, undefined, undefined, sessionStorage);
  return token;
};

/**
 * @description Grabs the current genesys cloud environment available.
 * @return {string} 
 */
export const gcEnvironment = () => {
  const environment = getStorageItem(OrganizationConstants.UrlAuthShort, undefined, undefined, sessionStorage);
  return environment;
};

export const getClientOrg = () => {
  const clientOrg = getStorageItem(AuthenticationConstants.ClientOrg, undefined, undefined, sessionStorage);
  return clientOrg;
};

export const subscriptionBaseUri = () => {
  let result = ''
  const dev: string = 'dev'
  switch (dev) {
    case 'test': 
      result = 'https://api.customer-subscription-manager.genesyspsdevops.com/test';
      break;
    case 'production': 
      result = 'https://api.customer-subscription-manager.genesyspsdevops.com/prod';
      break;
    case 'sandbox': 
      result = 'https://api.customer-subscription-manager.genesyspsdevops.com/sandbox';
      break;            
    default: 
      result = 'https://api.customer-subscription-manager.genesyspsdevops.com/dev';
      break;
  }
  return result;
};

/**
 * @async
 * @description async wrapper for the fetch event api.
 * @param {string} url string | url that's being called
 * @param {object} options object | fetch options
 * @param {string} token string | token for authentication
 * @param {boolean} auth bool | sets the auth header (default true)
 * @param {boolean} cookies bool | adds "credentials: 'include'" to the options (default false)
 * @param {object} headerOptions object | any additional header required objects
 * @param {boolean} noContentType bool | turn off the contentType of the request
 * @param {string} contentType string | sets content type in the header (default 'application/json')
 * @param {boolean} noResponseData bool | does not try to get the response json (default false)
 * @return {Promise<StandardResponse>} standard response
 */
export const fetchWrap = async ({
  url,
  options,
  token,
  auth = true,
  cookies = false,
  headerOptions = {},
  noContentType = false,
  contentType = 'application/json',
  noResponseData = false
}: FetchWrapParams): Promise<StandardResponse> => {
  
  // Clone StandardResponse and StandardHeaders to avoid mutation
  const standardResponse: StandardResponse = { ...defaultStandardResponse };
  const standardHeader: Record<string, string> = { ...defaultStandardHeaders };

  // Set content type if applicable
  if (contentType && !noContentType) {
    standardHeader['Content-Type'] = contentType;
  } else if (noContentType) {
    delete standardHeader['Content-Type'];
  }

  // Set authorization header if auth is enabled
  standardHeader['Authorization'] = auth ? `Bearer ${token}` : '';

  // Add credentials if cookies are enabled
  if (cookies) {
    options = {
      ...options,
      credentials: 'include',
    };
  }
  
  // Make the fetch request
  const response = await fetch(url, {
    ...options,
    headers: {
      ...standardHeader,
      ...headerOptions,
    },
  });
  
  // Handle no-content response or when noResponseData is true
  if (response.status === StatusCodesConstants.NoContent || noResponseData) {
    standardResponse.data = {};
    console.log(`API response: ${response.status} (No content)`);
  } else if (response.status === StatusCodesConstants.Accepted) {
    standardResponse.data = {};  // Set empty data for accepted status
    console.log(`API response: ${response.status} (Accepted)`);
    
    // For 202 responses, we'll still return a rejected promise to trigger retry logic
    standardResponse.status = response.status;
    standardResponse.url = response.url;
    standardResponse.success = false;  // Mark as unsuccessful to trigger retry
    
    // Collect response headers
    response.headers.forEach((value, key) => {
      standardResponse.headers[key] = value;
    });
    
    return Promise.reject(standardResponse);
  } else {
    try {
      standardResponse.data = await response.json();
      console.log(`API response: ${response.status} with data`);
    } catch (error) {
      console.log(`Failed to parse JSON response: ${error}`);
      standardResponse.data = {};
    }
  }

  // Populate the standard response
  standardResponse.status = response.status;
  standardResponse.url = response.url;
  standardResponse.success = response.ok;  

  // Collect response headers
  response.headers.forEach((value, key) => {
    standardResponse.headers[key] = value;
  });

  // Handle non-OK responses
  if (!response.ok) {
    console.log(`API error response: ${response.status}`);
    return Promise.reject(standardResponse);
  }

  standardResponse.success = true;
  return standardResponse;
};

const fetchWrapModule = {
  fetchWrap,
  baseUri,
  gcBaseUri,
  gcEnvironment,
  subscriptionBaseUri,
  getClientOrg
};

export default fetchWrapModule;